import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import PersonIcon from '@atlaskit/icon/core/person';
import { fg } from '@atlaskit/platform-feature-flags';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	useIsConfigLoaded,
} from '../../../../../controllers/store/bootstrap';
import { filter as leadStatusFilter } from '../../../../../controllers/store/search/filters/lead-status';

import { i18n } from './messages';

const LeadStatusFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: leadStatusFilter.id,
	});
	const shouldEagerLoadOptions = fg('rovo-fps-3p-ux-sf-lead-status-filter');
	const isRetryOnConfigLoadEnabled = fg('rovo-fps-3p-ux-sf-retry-on-config-load');
	const isConfigLoaded = useIsConfigLoaded();

	// See SalesforceStageFilter for the rationale: when the retry FF is on, hold the
	// "did mount" flip until isConfigLoaded is true so the bootstrap-config race
	// cannot leave the filter permanently empty.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!shouldEagerLoadOptions || hasLoadedInitialOptions.current) {
			return;
		}
		if (isRetryOnConfigLoadEnabled && !isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldEagerLoadOptions, isRetryOnConfigLoadEnabled, isConfigLoaded]);

	// Hide the filter entirely when the feature gate is off — matching the
	// SalesforceStageFilter pattern. Without this guard the filter would render
	// (empty) because its store `products` list always contains Salesforce.
	if (!filterData || !shouldEagerLoadOptions) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={leadStatusFilter.id}
			label={formatMessage(i18n.leadStatusFilterLabel)}
			noOptionsMessage={formatMessage(i18n.leadStatusFilterNoOptions)}
			iconBefore={<PersonIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => {
				return setOption(option);
			}}
			clearOptions={() => {
				return clearOptions();
			}}
		/>
	);
};

export default LeadStatusFilter;
